import { useEffect, useState } from 'react';
import EntityType from '@/shared/enums/EntityType';
import { useScenarioContext, useTagContext } from '@/shared/providers';
import mapAggregateActivities from '@/shared/utils/mapAggregateActivities';

/**
 * @param {Array} tagList
 * @param {Boolean} inverse
 * @return {Object}
 */
const useScenarioActivities = (
  {
    filter,
    includeDisabledActivities,
    sortKey,
    sortType,
    tagList,
    inverse,
    useComparison,
  } = {
    sortKey: '__aggregateHours',
    sortType: 'DESC',
    includeDisabledActivities: false,
    useComparison: false,
  }
) => {
  const {
    includeNested,
    scenario: primary,
    snapshotEntityMap: primaryEntityMap,
    comparisonScenario,
    comparisonScenarioEntityMap,
  } = useScenarioContext();
  const scenario = useComparison ? comparisonScenario : primary;
  const snapshotEntityMap = useComparison
    ? comparisonScenarioEntityMap
    : primaryEntityMap;
  const { tagMap } = useTagContext();

  const [activities, setActivities] = useState();
  const [groups, setGroups] = useState();
  const [unallocated, setUnallocated] = useState();
  const [metrics, setMetrics] = useState();
  const [tags, setTags] = useState();

  useEffect(() => {
    if (!scenario?.entity || !tagMap?.size) {
      return;
    }

    if (tagList) {
      const tags = new Map();

      tagList.forEach((id) => {
        const tag = tagMap.get(id);
        if (tag) {
          tags.set(id, { ...tag });
        }
      });

      setTags(tags);
    }

    const aggregatedActivities = mapAggregateActivities({
      design: snapshotEntityMap,
      filter,
      includeDisabledActivities,
      inverse,
      scenario,
      tagList,
    });

    const { activities, groups, unallocated, ...metricData } =
      aggregatedActivities;

    const sortedActivities = Array.from(activities.values()).sort((a, b) => {
      return b[sortKey] - a[sortKey];
    });

    if (sortType === 'ASC') {
      sortedActivities.reverse();
    }

    setActivities(sortedActivities);
    if (groups?.size) {
      setGroups(
        Array.from(groups?.values()).sort((a, b) => {
          return (
            b.__aggregateHours / b.__metrics.self.total.hours -
            a.__aggregateHours / a.__metrics.self.total.hours
          );
        })
      );
    }
    setUnallocated(
      Array.from(unallocated.values()).sort((a, b) => {
        return b.__aggregateHours - a.__aggregateHours;
      })
    );
    setMetrics(metricData);
  }, [
    includeNested,
    scenario?.entity?.uuid,
    scenario?.entity?.__uuid,
    scenario?.entity?.updated_at,
    JSON.stringify(
      scenario?.entity?.__metrics?.visible ??
        scenario?.entity?.__metrics?.self?.visible
    ),
    JSON.stringify(tagList),
    JSON.stringify(filter),
    tagMap,
  ]);

  return {
    tags,
    activities,
    activityCount: primaryEntityMap?.get(EntityType.ACTIVITY)?.size ?? 0,
    groups,
    unallocated,
    metrics,
  };
};

export default useScenarioActivities;
